import { useState } from "react";
import { Redirect } from "react-router-dom";
import Parse from "parse";

import {
  Card,
  CardContent,
  Link,
  Typography,
  TextField,
  Button,
  Snackbar,
  IconButton,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

export const LoginComponent = ({ loggedIn, setUser }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleLogin();
    }
  };

  /**
   * Takes care of logging in a user.
   *
   * We get the username and password and submit it to the parse-server
   * backend called desdapi. After the backend acknowledges our credentials,
   * we redirect the user to the requested page it tried to visit before being
   * redirected to the login page.
   *
   * If the credentials are invalid, we display an error (using the snackbar
   * pop-up-banner) and allow the user to try again.
   *
   *
   * ## NOTE:
   * This is the updated version of handleLogin(). It also replaces the
   * `onLoggedIn()` method of the main `App` component.
   * The old one used the legacy parse.php system, which was really, really
   * bad. Now, we use a custom desda authAdapter in parse-server (see
   * desda/desdapi) to handle authentication. In order to use this, we need
   * to sign in with this provider. We do this by using Parse.User.logInWith().
   * This is explained in more detail in the desdapi repo (see desdAuth.js and
   * devAuth.js for the implementation of the resective auth adapters).
   */
  const handleLogin = () => {
    // disable the login button while we are busy
    setIsLoggingIn(true);

    // the authData we need to supply; id and password are required,
    // anything else than those two are an error
    const creds = {
      id: username,
      password: password,
    };

    // log in with the 'desda' authProvider; see desda/desdapi repo for more
    // info
    Parse.User.logInWith("desda", { authData: creds }).then(
      // When the Promise resolves, this function gets triggered with
      // the object corresponding to the user that has been logged in.
      (user) => {
        // we succesfully authenticated!
        console.log("Logged in as:", user.getUsername(), user);

        // inform the global state of who we are now; user
        // being the Parse.User object returned by Parse.User.logInWith
        setUser(user);

        // we are done loggin in so we can unblock the login button
        setIsLoggingIn(false);

        /*
         * Get the url we went to before being logged in.
         * If a user is not logged in, we get redirected to /login
         * the original path we requested is then put in the `req`
         * url parameter.
         *
         * Go to https://app.desda.org/events.
         * We are not logged in so we get send to /login
         * The url now looks like:
         * https://app.desda.org/login?req=/events
         *
         * Now we retrieve this path to send us back there
         * after we have logged in.
         */
        let url_string = window.location.href;
        let url = new URL(url_string);
        let requestedPage = url.searchParams.get("req");
        if (!requestedPage) {
          // if we go to https://app.desda.org
          // redirect to https://app.desda.org/
          requestedPage = "/";
        }
        // actually send us to the page we originally requested
        window.location.replace(requestedPage);
      },
      (error) => {
        // authentication failed
        console.log(
          "Authentication failed with error code " +
            `${error.code}: ${error.message}`,
        );

        // return the state to how it was before trying to log in
        /*
         * Ja, ik hoor je denken: wtf?
         * Als ik het goed heb begrepen, is de "snackbar" de kleine
         * pop-up-banner die de desdapp gebruikt. Als je credentials
         * incorrect zijn, verschijnt ie met de mededeling dat je
         * gebruikersnaam en wachtwoord niet kloppen.
         * `snackbarOpen` controleert of de banner getoont wordt.
         * We willen hem nu laten zien, dus zetten we
         * `snackbarOpen: true`.
         */
        setSnackbarOpen(true);
        setIsLoggingIn(false);
      },
    );
  };

  return (
    <div>
      {!loggedIn ? (
        <Card className="paper-card login-card">
          <form>
            <CardContent className="inlog-card">
              <div className="inlog-card">
                <Typography gutterBottom variant="h5" component="h1">
                  Inloggen
                </Typography>
                <Typography component="p">
                  Log hieronder in met je gebruikersnaam en wachtwoord van de
                  Desda-site.{" "}
                </Typography>
                <Typography component="p" fontWeight="fontWeightMedium">
                  LET OP: JE KUNT NIET INLOGGEN MET JE E-MAILADRES.
                </Typography>
                <Typography component="p">
                  Je kunt op{" "}
                  <a href="https://www.desda.org/account">
                    www.desda.org/account
                  </a>{" "}
                  je gebruikersnaam vinden.
                </Typography>
                <TextField
                  id="username"
                  label="Gebruikersnaam"
                  value={username}
                  onChange={(event) => {
                    setUsername(event.target.value);
                  }}
                  variant="outlined"
                  margin="normal"
                  autoComplete="username"
                />
                <TextField
                  id="password"
                  label="Wachtwoord"
                  value={password}
                  onChange={(event) => {
                    setPassword(event.target.value);
                  }}
                  type="password"
                  variant="outlined"
                  autoComplete="current-password"
                  margin="normal"
                  onKeyPress={handleKeyPress}
                />
                <Button
                  className="login-button"
                  variant="contained"
                  color="primary"
                  onClick={handleLogin}
                  disabled={isLoggingIn}
                >
                  Inloggen
                </Button>
                <Snackbar
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                  open={snackbarOpen}
                  autoHideDuration={6000}
                  onClose={handleSnackbarClose}
                  ContentProps={{
                    "aria-describedby": "message-id",
                  }}
                >
                  <Alert severity="error">
                    Gebruikersnaam of wachtwoord incorrect.
                    <IconButton
                      key="close"
                      aria-label="Close"
                      color="inherit"
                      style={{ padding: "4px" }}
                      onClick={handleSnackbarClose}
                      size="large"
                    >
                      <CloseIcon />
                    </IconButton>
                  </Alert>
                </Snackbar>

                <Typography style={{ marginTop: "8px" }} component="p">
                  Lukt het niet om in te loggen? Stuur dan even een mailtje naar
                  www-cie@desda.org of een appje naar Eskil (06 HYPE 481841).
                </Typography>
                <Typography style={{ color: "#888" }} component="p">
                  Vul de logische waarde in voor HYPE om te bewijzen dat je geen
                  robot bent.
                </Typography>
              </div>
            </CardContent>
          </form>
        </Card>
      ) : (
        <Redirect to="/settings" />
      )}{" "}
    </div>
  );
};

export default LoginComponent;
